// Global Instructions Rule Applied!

import React, { useState, useEffect, useCallback, useMemo, forwardRef } from 'react'
import { debounce } from 'lodash'
import Select from '../../Select.jsx'
import { useDealSearch } from '../../../hooks_deprecated/useDealSearch.js'
import { InputSize, FieldValue } from '../types.ts'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

export interface DealSelectProps {
  value: FieldValue
  onChange?: (value: string | string[] | FieldValue, option?: SelectListEntry | SelectListEntry[] | null) => void
  onSelect?: (value: string, option?: SelectListEntry) => void
  onClear?: () => void
  mode?: 'form' | 'inline' | 'cell' | 'single' | 'multiple'
  selectMode?: 'single' | 'multiple'
  required?: boolean
  placeholder?: string
  size?: InputSize
  error?: boolean
  style?: React.CSSProperties
  className?: string
  autoFocus?: boolean
  onKeyDown?: (e: React.KeyboardEvent) => void
  disabled?: boolean
}

export const DealSelect = forwardRef<any, DealSelectProps>(
  (
    {
      value,
      onChange,
      onSelect,
      onClear,
      mode = 'form',
      selectMode = 'single',
      required = false,
      placeholder = 'Search deals...',
      size = 'small',
      error = false,
      style = {},
      className = '',
      autoFocus,
      onKeyDown,
      disabled,
      ...selectProps
    },
    ref
  ) => {
    const {
      searchDeals,
      loading: dealSearchLoading,
      results: dealSearchResults,
      clearResults: clearDealResults,
      hasSearched: dealHasSearched
    } = useDealSearch()

    const isMultiple = selectMode === 'multiple'
    const [selectedDeal, setSelectedDeal] = useState<SelectListEntry | null>(null)
    const [selectedDeals, setSelectedDeals] = useState<SelectListEntry[]>([])

    const initialValueOptions = useMemo<SelectListEntry[]>(() => {
      if (!value) return []

      if (isMultiple) {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            if (typeof v === 'object' && v.value !== undefined && v.label) {
              return {
                label: v.label,
                value: v.value,
                name: v.name || v.label
              }
            }
            return {
              label: v,
              value: v,
              name: v
            }
          })
        }
        return []
      } else {
        if (typeof value === 'object' && value && (value as any).value !== undefined && (value as any).label) {
          const v = value as any
          return [
            {
              label: v.label,
              value: v.value,
              name: v.name || v.label
            }
          ]
        }
        return []
      }
    }, [value, isMultiple])

    const initializeDealSelection = useCallback(
      (dealVal: any) => {
        if (isMultiple) {
          if (Array.isArray(dealVal) && dealVal.length > 0) {
            const deals = dealVal.map((v: any) => {
              if (typeof v === 'object' && v.value !== undefined) {
                return {
                  label: v.label || v.name || v.value,
                  value: v.value,
                  name: v.name || v.label || v.value
                }
              }
              return {
                label: v,
                value: v,
                name: v
              }
            })
            setSelectedDeals(deals)
          } else {
            setSelectedDeals([])
          }
        } else {
          let dealId: string = ''
          let dealLabel: string = ''

          if (typeof dealVal === 'object' && dealVal !== null) {
            dealId = (dealVal as any).value || (dealVal as any).id || ''
            dealLabel = (dealVal as any).label || (dealVal as any).name || ''
          } else if (dealVal) {
            dealId = String(dealVal)
          }

          const currentValue = selectedDeal?.value || ''
          const valueChanged = dealId !== currentValue

          if (dealVal && (!selectedDeal || valueChanged)) {
            if (dealLabel && dealId) {
              const selectListEntry: SelectListEntry = {
                label: dealLabel,
                value: dealId,
                name: dealLabel
              }
              setSelectedDeal(selectListEntry)
            } else if (dealId) {
              const existingDeal = dealSearchResults.find((deal: any) => deal.value === dealId || deal.label === dealId)

              if (existingDeal) {
                const selectListEntry: SelectListEntry = {
                  label: existingDeal.label,
                  value: existingDeal.value,
                  name: existingDeal.label
                }
                setSelectedDeal(selectListEntry)
              } else {
                const tempDeal: SelectListEntry = {
                  label: dealLabel || dealId,
                  value: dealId,
                  name: dealLabel || dealId
                }
                setSelectedDeal(tempDeal)
              }
            }
          } else if (!dealVal) {
            setSelectedDeal(null)
          }
        }
      },
      [dealSearchResults, selectedDeal, isMultiple]
    )

    useEffect(() => {
      initializeDealSelection(value)
    }, [value, initializeDealSelection])

    useEffect(() => {
      if (isMultiple) {
        setSelectedDeals(initialValueOptions)
      }
    }, [initialValueOptions, isMultiple])

    const handleDealSelect = useCallback(
      (selectedValue: any, option: any) => {
        if (isMultiple) {
          if (Array.isArray(selectedValue)) {
            const transformedValues = selectedValue.map((val: any) => {
              const existingDeal = Array.isArray(value)
                ? value.find((v: any) => {
                    const vValue = typeof v === 'object' && v.value !== undefined ? v.value : v
                    return vValue === val
                  })
                : null

              const existingOption =
                dealSearchResults.find((opt: any) => opt.value === val) ||
                initialValueOptions.find((opt) => opt.value === val)

              return {
                label: existingDeal?.label || existingOption?.label || existingOption?.name || val,
                value: val,
                name: existingDeal?.name || existingOption?.name || existingOption?.label || val
              }
            })
            setSelectedDeals(transformedValues)
            onChange?.(transformedValues)
          } else {
            setSelectedDeals([])
            onChange?.([])
          }
        } else {
          if (option) {
            setSelectedDeal(option)
            onChange?.(selectedValue, option)
            onSelect?.(selectedValue, option)
          }
        }
      },
      [onChange, onSelect, isMultiple, value, dealSearchResults, initialValueOptions]
    )

    const handleClear = useCallback(() => {
      if (isMultiple) {
        setSelectedDeals([])
        onChange?.([])
      } else {
        setSelectedDeal(null)
        onChange?.('', null)
      }
      clearDealResults()
      onClear?.()
    }, [onChange, onClear, clearDealResults, isMultiple])

    const debouncedSearch = useMemo(
      () =>
        debounce((searchValue: string) => {
          if (searchValue && searchValue.length >= 2) {
            searchDeals(searchValue)
          }
        }, 300),
      [searchDeals]
    )

    const handleSearch = useCallback(
      (searchValue: string) => {
        if (!searchValue) {
          clearDealResults()
        } else {
          debouncedSearch(searchValue)
        }
      },
      [debouncedSearch, clearDealResults]
    )

    const transformedResults: SelectListEntry[] = dealSearchResults.map((result: any) => ({
      label: result.label,
      value: result.value,
      name: result.label
    }))

    const getDisplayValue = () => {
      if (!value) return undefined

      if (isMultiple) {
        if (Array.isArray(value)) {
          return value.map((v: any) => {
            if (typeof v === 'object' && v.value !== undefined) {
              return v.value
            }
            return typeof v === 'string' ? v : v
          })
        }
        return []
      } else {
        if (typeof value === 'object' && value && (value as any).value !== undefined) {
          return (value as any).value
        }
        return typeof value === 'object' && value ? (value as any).value : value
      }
    }

    const displayOptions = useMemo(() => {
      if (isMultiple) {
        if (transformedResults.length > 0) {
          const searchValues = new Set(transformedResults.map((opt) => opt.value))
          const mergedInitialOptions = initialValueOptions.filter((opt) => !searchValues.has(opt.value))
          return [...transformedResults, ...mergedInitialOptions]
        }
        return initialValueOptions
      } else {
        if (selectedDeal) {
          return [selectedDeal, ...transformedResults.filter((result) => result.value !== selectedDeal.value)]
        }
        return transformedResults
      }
    }, [transformedResults, selectedDeal, initialValueOptions, isMultiple])

    const multipleDisplayValue = useMemo(() => {
      if (isMultiple) {
        if (selectedDeals.length > 0) {
          return selectedDeals.map((d) => d.value)
        }
        return initialValueOptions.map((opt) => opt.value)
      }
      return undefined
    }, [isMultiple, selectedDeals, initialValueOptions])

    const options = displayOptions

    const placeholderText = required ? `${placeholder}` : placeholder

    return (
      <Select
        ref={ref}
        mode={isMultiple ? 'multiple' : undefined}
        value={isMultiple ? multipleDisplayValue : selectedDeal ? selectedDeal.value : undefined}
        onChange={handleDealSelect}
        onClear={handleClear}
        onSearch={handleSearch}
        loading={dealSearchLoading}
        options={options}
        placeholder={placeholderText}
        size={size}
        style={{
          width: '100%',
          ...style,
          borderColor: style.borderColor || (error ? '#ff4d4f' : undefined)
        }}
        className={className}
        status={error ? 'error' : undefined}
        filterOption={false}
        notFoundContent={
          dealSearchLoading ? 'Searching...' : dealHasSearched ? 'No deals found' : 'Type to start searching'
        }
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
        allowClear
        {...selectProps}
      />
    )
  }
)

DealSelect.displayName = 'DealSelect'
